import React, { useContext, useEffect, useState } from 'react';
import ResourceIndex from '../Shared/ResourceIndex/ResourceIndex';
import { Link, Redirect, useLocation } from 'react-router-dom';
import { ConfirmationModal, Modal } from '@stormwater-software/lib-ui';
import divisionApi from '../../utils/api/division';
import { toast } from 'react-toastify';
import useResource from '../../hooks/resource';
import {
  FormContext,
  FormContextProvider,
  FormSchemaFields,
} from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { FormModal } from '../Shared/form';
import Popper from '../Shared/Popper/Popper';

const AddProjectToAreaModalUI = ({ areaId }: { areaId: number }) => {
  const divisionId = useLocation().pathname.split('/')[2];
  const formContext = useContext(FormContext);
  const [clientList, setClientList] = useState<any>();
  const [projectList, setProjectList] = useState<any>();

  useEffect(() => {
    const fetchClients = async () => {
      const clients = await divisionApi.clients.index(Number(divisionId));
      setClientList(
        clients.map((client: any) => ({ id: client.id, name: client.name })),
      );
    };
    fetchClients();
  }, [divisionId]);

  useEffect(() => {
    formContext.set('projectIds', []);
    if (!formContext.value.clientId) {
      setProjectList([]);
      return;
    }
    const fetchProjects = async () => {
      const projects = await divisionApi.areas.clientProjects.index(
        formContext.value.clientId,
        areaId,
      );
      setProjectList(
        projects.map((project: any) => ({
          id: project.id,
          name: project.name,
        })),
      );
    };
    fetchProjects();
  }, [formContext.value.clientId]);

  return (
    <FormSchemaFields
      className={'findings-form'}
      schema={{
        clientId: {
          controlType: UIControlType.select,
          labelKey: 'name',
          valueKey: 'id',
          label: 'Clients',
          options: clientList,
          noOptionMessage: 'No clients found',
          placeholder: 'Search for clients',
          validation: { required: true },
        },
        projectIds: {
          controlType: UIControlType.customTagSelect,
          labelKey: 'name',
          valueKey: 'id',
          isMulti: true,
          openOnFocus: true,
          label: 'Projects',
          options: projectList,
          noOptionMessage: 'No projects found',
          placeholder: 'Search for projects',
          menuPlacement: 'top',
          showSelectAll: true,
          showClearAll: true,
          validation: { required: true },
        },
      }}
      formData={formContext.value}
      onChange={formContext.replace}
    />
  );
};

const AddProjectToAreaModal = ({
  areaId,
  onSubmit,
  onClose,
  justSubmit,
}: {
  areaId: number;
  onSubmit: any;
  onClose: any;
  justSubmit?: any;
}) => {
  const [projectsWithOtherAreas, setProjectsWithOtherAreas] = useState<any>([]);
  const [clientId, setClientId] = useState<number | null>(null);
  const [projectIds, setProjectIds] = useState<number[]>([]);

  const handleSubmit = (formState: any) => {
    divisionApi.areas.projects
      .create(
        areaId,
        formState.projectIds.map((ele: any) => ele.id),
      )
      .then((data) => {
        if (data.hasOtherArea) {
          setProjectsWithOtherAreas(data.projectNameIds);
          setClientId(formState.clientId);
          setProjectIds(formState.projectIds);
          justSubmit();
        } else {
          onSubmit();
          toast.success('Projects added to area!');
        }
      });
    return Promise.resolve();
  };

  const handleResubmit = () => {
    divisionApi.areas.projects
      .create(
        areaId,
        projectsWithOtherAreas.map((ele: any) => ele.id),
        true,
      )
      .then(() => {
        toast.success('Projects reassigned successfully!');
        setProjectsWithOtherAreas([]);
        onSubmit();
      });
  };

  return (
    <FormContextProvider
      initialValue={{
        projectIds: projectIds,
        clientId: clientId,
      }}
    >
      <FormModal
        modalProps={{
          title: 'Add Projects',
          classes: 'add-users',
          dismissOnEsc: true,
        }}
        showCancel={false}
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitText='Add Projects'
      >
        <AddProjectToAreaModalUI areaId={areaId} />
      </FormModal>
      <Modal
        show={projectsWithOtherAreas.length > 0}
        handleSubmit={handleResubmit}
        handleClose={() => setProjectsWithOtherAreas([])}
        title='Project(s) are already assigned to other areas'
        submitBtnText='Proceed'
      >
        <p>The following projects are already assigned to other areas:</p>
        <ul>
          {projectsWithOtherAreas.map((project: any, index: number) => (
            <li key={index}>{project.name}</li>
          ))}
        </ul>
        <p>
          If you proceed, the projects will be removed from their current areas
          and added to this Area
        </p>
      </Modal>
    </FormContextProvider>
  );
};

const Area = () => {
  const location = useLocation();
  const areaId = location.pathname.split('/')[4];
  const divisionId = location.pathname.split('/')[2];
  const { query: apiQuery } = useResource({
    resource: 'areaProjects',
    disablePagination: false,
    divisionId: Number(divisionId),
    areaId: Number(areaId),
  });

  const { refetch } = apiQuery;

  const [toDivision, setToDivision] = useState<boolean>(false);
  const [showDeleteForm, setShowDeleteForm] = useState(false);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [showDeleteAreaModal, setShowDeleteAreaModal] = useState(false);

  const handleProjectDelete = async () => {
    divisionApi.areas.projects
      .remove(Number(areaId), selectedProject.project_id)
      .then(() => {
        toast.success('Project removed from this area!');
        refetch();
      })
      .catch(() => {
        toast.error(
          'An error occurred while removing the project from this area.',
        );
      });
    setShowDeleteForm(false);
  };

  if (toDivision) {
    return <Redirect to={`/divisions/${divisionId}/resources/areas`} push />;
  }

  return (
    <div className='areas-index'>
      <ResourceIndex
        resourceName='areaProjects'
        resourceQueryProps={{
          divisionId: divisionId,
          areaId: Number(areaId),
        }}
        resourceTableHeaders={[
          ['Project Name', 'project_name'],
          ['Client Name', 'client_name'],
          ['Inspector', 'inspector'],
          ['Interval', 'inspection_interval'],
          ['Type', 'project_type'],
          ['Action', 'action'],
        ]}
        resourceSearchProps={['project_name', 'client_name', 'inspector']}
        enablePagination
        enableRead
        enableEdit
        enableClick={false}
        enableCallToAction
        enableOverflow
        searchInputPlaceholder='Search Projects...'
        callToActionContent='Add Projects'
        renderCallToAction={(handleClose, pushData) => (
          <AddProjectToAreaModal
            areaId={Number(areaId)}
            onClose={handleClose}
            onSubmit={(data: any) => {
              pushData(data);
              refetch();
              handleClose();
            }}
            justSubmit={(data: any) => {
              pushData(data);
              refetch();
            }}
          />
        )}
        renderRowCell={(project, propName) => {
          switch (propName) {
            case 'action':
              return (
                <>
                  <Link
                    to={`/divisions/${divisionId}/projects/${project.project_id}/inspections`}
                  >
                    <button className='button-small bg-primary'>
                      View Project
                    </button>
                  </Link>
                  <button
                    className='warn icon-only-button tiny outline pad-left'
                    title='remove contact'
                    onClick={() => {
                      setSelectedProject(project);
                      setShowDeleteForm(true);
                    }}
                  >
                    <i className='fa fa-trash' />
                  </button>
                </>
              );
            default:
              return ResourceIndex.defaultProps.renderRowCell(
                project,
                propName,
              );
          }
        }}
        renderOverflow={(coordinates: any, handleClose: any) => (
          <React.Fragment>
            <Popper coordinates={coordinates} handleClose={handleClose}>
              <ul>
                <li style={{ padding: 0 }}>
                  <button
                    onClick={() => {
                      setShowDeleteAreaModal(true);
                      handleClose();
                    }}
                    className='button-transparent'
                  >
                    <i className='fa fa-trash pad-right' />
                    Remove Area
                  </button>
                </li>
              </ul>
            </Popper>
          </React.Fragment>
        )}
        hideCallToActionInMobile={false}
      />

      <ConfirmationModal
        show={showDeleteForm}
        handleConfirm={handleProjectDelete}
        handleClose={() => setShowDeleteForm(false)}
        title='Remove Project'
        subTitle={`Are you sure you want to remove ${
          selectedProject ? selectedProject.project_name : ''
        } from this area?`}
        buttonText='Remove Project'
      />

      <ConfirmationModal
        show={showDeleteAreaModal}
        handleConfirm={() => {
          divisionApi.areas
            .delete(Number(divisionId), [Number(areaId)])
            .then(() => {
              toast.success('Area removed from this Division!');
              setToDivision(true);
              setShowDeleteAreaModal(false);
            })
            .catch(() => {
              toast.error('Error deleting area.');
            });
        }}
        handleClose={() => setShowDeleteAreaModal(false)}
        title='Remove Area'
        subTitle='Are you sure you want to remove this area from the division? All linked projects will be unassociated.'
        buttonText='Remove Area'
      />
    </div>
  );
};

export default Area;
